/**
 * Currency Converter - KRW to USD frontend display
 *
 * Reads original KRW prices from data-original-price attributes (in cents)
 * and renders them as USD when active.
 *
 * State stored in localStorage('anchor_currency') = 'KRW' | 'USD'
 *
 * Default: detected from URL locale (/ko/ → KRW, otherwise → USD)
 * Manual override: window.AnchorCurrency.set('USD' | 'KRW')
 */

(function () {
  var STORAGE_KEY = 'anchor_currency';
  var COOKIE_KEY = 'anchor_currency';

  /* Hardcoded exchange rate. Update periodically.
     1 USD = 1400 KRW */
  var KRW_TO_USD = 1 / 1400;

  /* Round USD to nearest .99 ending */
  function roundUsd(usd) {
    if (usd <= 0) return 0;
    var floored = Math.floor(usd);
    return floored + 0.99;
  }

  function formatUsd(usd) {
    if (usd <= 0) return '$0.00';
    return '$' + usd.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  function formatKrw(krw) {
    return '\u20A9' + Math.round(krw).toLocaleString('en-US') + ' KRW';
  }

  function setCookie(name, value) {
    var expires = new Date();
    expires.setTime(expires.getTime() + 365 * 24 * 60 * 60 * 1000);
    document.cookie = name + '=' + encodeURIComponent(value) +
      ';expires=' + expires.toUTCString() + ';path=/;SameSite=Lax';
  }

  function getCookie(name) {
    var nameEQ = name + '=';
    var ca = document.cookie.split(';');
    for (var i = 0; i < ca.length; i++) {
      var c = ca[i].trim();
      if (c.indexOf(nameEQ) === 0) {
        return decodeURIComponent(c.substring(nameEQ.length));
      }
    }
    return null;
  }

  function detectDefault() {
    /* Locale-based default: ko locale → KRW, otherwise USD */
    var path = window.location.pathname;
    if (path.startsWith('/ko/') || path === '/ko') return 'KRW';
    if (document.documentElement.lang && document.documentElement.lang.toLowerCase().indexOf('ko') !== -1) return 'KRW';
    return 'USD';
  }

  function getCurrency() {
    try {
      var stored = localStorage.getItem(STORAGE_KEY) || getCookie(COOKIE_KEY);
      if (stored === 'USD' || stored === 'KRW') return stored;
    } catch (e) {}
    return detectDefault();
  }

  function setCurrency(currency) {
    if (currency !== 'USD' && currency !== 'KRW') return;
    try {
      localStorage.setItem(STORAGE_KEY, currency);
    } catch (e) {}
    setCookie(COOKIE_KEY, currency);
    convertAll();
    document.dispatchEvent(new CustomEvent('anchor:currency-changed', { detail: { currency: currency } }));
  }

  /**
   * Convert a single price element. The element must have:
   *   - data-original-price="{cents}"  (KRW value in cents, i.e. price * 100)
   *   - optional data-price-format="default|from|range" for prefix handling
   */
  function convertPriceElement(el) {
    var raw = el.getAttribute('data-original-price');
    if (raw === null || raw === '') return;
    var cents = parseFloat(raw);
    if (isNaN(cents)) return;
    var krw = cents / 100;
    var currency = getCurrency();

    var formatted;
    if (currency === 'USD') {
      var usd = roundUsd(krw * KRW_TO_USD);
      formatted = formatUsd(usd);
    } else {
      formatted = formatKrw(krw);
    }

    /* Preserve prefix like "From " if present */
    var prefix = el.getAttribute('data-price-prefix') || '';
    el.textContent = prefix + formatted;
  }

  /**
   * Convert text-based money strings (legacy fallback for elements that
   * don't have data-original-price). Looks for "₩123,000" or "123,000원" patterns.
   */
  function convertTextNode(el) {
    if (el.dataset.currencyProcessed === '1') return;
    var text = el.textContent;
    if (!text) return;

    /* Match KRW patterns: ₩123,000 / 123,000원 / KRW 123,000 */
    var krwRegex = /(?:\u20A9|KRW\s?)?([\d,]+)(?:\s?(?:\uC6D0|KRW))?/;
    var match = text.match(krwRegex);
    if (!match) return;

    var num = parseFloat(match[1].replace(/,/g, ''));
    if (isNaN(num) || num < 100) return; /* skip small numbers like ratings */

    var currency = getCurrency();
    if (currency === 'KRW') {
      el.dataset.currencyProcessed = '1';
      return;
    }

    var usd = roundUsd(num * KRW_TO_USD);
    el.textContent = text.replace(match[0], formatUsd(usd));
    el.dataset.currencyProcessed = '1';
  }

  function convertAll(root) {
    var scope = root || document;

    /* Primary path: explicit data-original-price elements */
    var explicitEls = scope.querySelectorAll('[data-original-price]');
    explicitEls.forEach(convertPriceElement);

    /* Update body class so CSS can react */
    var currency = getCurrency();
    document.body.classList.toggle('anchor-currency-usd', currency === 'USD');
    document.body.classList.toggle('anchor-currency-krw', currency === 'KRW');
  }

  /* Watch for dynamically added price elements (cart drawer reload, AJAX, etc.) */
  function observeDom() {
    var observer = new MutationObserver(function (mutations) {
      var hasNewPrices = false;
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.hasAttribute && node.hasAttribute('data-original-price')) {
            hasNewPrices = true;
          } else if (node.querySelector && node.querySelector('[data-original-price]')) {
            hasNewPrices = true;
          }
        });
      });
      if (hasNewPrices) convertAll();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* Public API */
  window.AnchorCurrency = {
    get: getCurrency,
    set: setCurrency,
    convert: convertAll,
    rate: KRW_TO_USD,
    format: function (krwAmount) {
      var currency = getCurrency();
      if (currency === 'USD') return formatUsd(roundUsd(krwAmount * KRW_TO_USD));
      return formatKrw(krwAmount);
    }
  };

  function init() {
    convertAll();
    observeDom();
    /* Notify other scripts (e.g. footer dropdowns) that currency is ready */
    document.dispatchEvent(new CustomEvent('anchor:currency-changed', {
      detail: { currency: getCurrency(), initial: true }
    }));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
